import {
  AsyncOp,
  Failure,
  Pending,
  Success,
} from "@indietabletop/appkit/async-op";
import { caughtValueToString } from "@indietabletop/appkit/caught-value";
import { useEffect, useState } from "react";
import { mask, object, string } from "superstruct";
import { config } from "../../config";
import { Army } from "../../domain/army";

async function createArmySnapshot(army: Army) {
  const response = await fetch(`${config.ITC_API_ORIGIN}/eternol/snapshots`, {
    method: "POST",
    body: JSON.stringify(army.data),
    headers: { "Content-Type": "application/json" },
  });

  if (!response.ok) {
    throw new Error("Couldn't create share link. (Response failed)");
  }

  return mask(await response.json(), object({ share_id: string() }));
}

function getShareLink(shareId: string) {
  return `${window.origin}/army/s/${shareId}`;
}

/**
 * Provided {@link Army}, returns a {@link AsyncOp} of Share URL, creating
 * the URL if necessary.
 *
 * Note that a Share ID already exists on the supplied army, the will be
 * no async operation — the data will be simple returned as success.
 */
export function useEnsureShareLink(army: Army) {
  const { share_id: shareId } = army.data;
  const [result, setShareLink] = useState<AsyncOp<string, string>>(
    shareId ? new Success(getShareLink(shareId)) : new Pending(),
  );

  useEffect(() => {
    async function effect() {
      if (!army.data.share_id) {
        try {
          const data = await createArmySnapshot(army);
          await army.setShareId(data.share_id);

          // Report success
          setShareLink(new Success(getShareLink(data.share_id)));
        } catch (error: unknown) {
          setShareLink(new Failure(caughtValueToString(error)));
        }
      }
    }

    void effect();
  }, [army]);

  return result;
}
